'use client';

/**
 * PercentageIndicator Component
 *
 * A color-coded percentage display with optional subtitle.
 * Positive values are green, negative are red, zero is neutral.
 */

import Box, { type BoxProps } from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { forwardRef } from 'react';
import { baseColors } from '../../theme/palette';
import { transientOptions } from '../../utils/styledUtils';

// ============================================================================
// Types
// ============================================================================

export interface PercentageIndicatorProps extends Omit<BoxProps, 'children'> {
  /**
   * Percentage value (e.g., -85 for -85%, 12 for +12%)
   */
  value: number;

  /**
   * Optional subtitle text (e.g., "15m current", "vs last week")
   */
  subtitle?: string;

  /**
   * Size variant
   * @default 'medium'
   */
  size?: 'small' | 'medium' | 'large';

  /**
   * Show + sign for positive values
   * @default false
   */
  showPositiveSign?: boolean;

  /**
   * Invert colors (positive=red, negative=green)
   * Useful for metrics where lower is better (e.g., error rate)
   * @default false
   */
  invertColors?: boolean;

  /**
   * Alignment
   * @default 'right'
   */
  align?: 'left' | 'center' | 'right';

  /**
   * Custom prefix (e.g., "$" for currency)
   */
  prefix?: string;

  /**
   * Custom suffix (defaults to "%")
   */
  suffix?: string;
}

// ============================================================================
// Size Configuration
// ============================================================================

const sizeConfig = {
  small: {
    valueFontSize: '1rem',
    valueFontWeight: 600,
    subtitleFontSize: '0.65rem',
  },
  medium: {
    valueFontSize: '1.5rem',
    valueFontWeight: 700,
    subtitleFontSize: '0.75rem',
  },
  large: {
    valueFontSize: '2rem',
    valueFontWeight: 700,
    subtitleFontSize: '0.85rem',
  },
};

// ============================================================================
// Color Helpers
// ============================================================================

type Sentiment = 'positive' | 'negative' | 'neutral';

const getSentiment = (value: number, invertColors: boolean): Sentiment => {
  if (value === 0) return 'neutral';
  const isPositive = value > 0;
  if (invertColors) {
    return isPositive ? 'negative' : 'positive';
  }
  return isPositive ? 'positive' : 'negative';
};

const getColor = (sentiment: Sentiment, isDark: boolean): string => {
  const colors = {
    positive: isDark ? baseColors.success[400] : baseColors.success[500],
    negative: isDark ? baseColors.error[400] : baseColors.error[500],
    neutral: isDark ? 'rgba(148, 163, 184, 0.9)' : 'rgba(100, 116, 139, 0.9)',
  };
  return colors[sentiment];
};

// ============================================================================
// Styled Components
// ============================================================================

interface StyledContainerProps {
  $align: 'left' | 'center' | 'right';
}

const StyledContainer = styled(
  Box,
  transientOptions,
)<StyledContainerProps>(({ $align }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: $align === 'left' ? 'flex-start' : $align === 'right' ? 'flex-end' : 'center',
}));

interface StyledValueProps {
  $size: 'small' | 'medium' | 'large';
}

const StyledValue = styled(
  Typography,
  transientOptions,
)<StyledValueProps>(({ $size }) => {
  const config = sizeConfig[$size];
  return {
    fontSize: config.valueFontSize,
    fontWeight: config.valueFontWeight,
    lineHeight: 1.2,
    fontVariantNumeric: 'tabular-nums',
  };
});

interface StyledSubtitleProps {
  $size: 'small' | 'medium' | 'large';
}

const StyledSubtitle = styled(
  Typography,
  transientOptions,
)<StyledSubtitleProps>(({ theme, $size }) => {
  const config = sizeConfig[$size];
  return {
    fontSize: config.subtitleFontSize,
    color: theme.palette.mode === 'dark' ? 'rgba(148, 163, 184, 0.7)' : 'rgba(100, 116, 139, 0.8)',
    lineHeight: 1.4,
    marginTop: 2,
  };
});

// ============================================================================
// Component
// ============================================================================

export const PercentageIndicator = forwardRef<HTMLDivElement, PercentageIndicatorProps>(
  (
    {
      value,
      subtitle,
      size = 'medium',
      showPositiveSign = false,
      invertColors = false,
      align = 'right',
      prefix = '',
      suffix = '%',
      sx,
      ...props
    },
    ref,
  ) => {
    const sentiment = getSentiment(value, invertColors);

    const formatValue = () => {
      const sign = value > 0 && showPositiveSign ? '+' : '';
      const absValue = Math.abs(value);
      // Format with appropriate decimal places
      const formattedNumber = Number.isInteger(absValue) ? absValue.toString() : absValue.toFixed(1);

      return `${value < 0 ? '-' : sign}${prefix}${formattedNumber}${suffix}`;
    };

    return (
      <StyledContainer ref={ref} $align={align} sx={sx} {...props}>
        <StyledValue
          $size={size}
          sx={(theme) => ({
            color: getColor(sentiment, theme.palette.mode === 'dark'),
          })}
        >
          {formatValue()}
        </StyledValue>
        {subtitle && <StyledSubtitle $size={size}>{subtitle}</StyledSubtitle>}
      </StyledContainer>
    );
  },
);

PercentageIndicator.displayName = 'PercentageIndicator';

export default PercentageIndicator;
